import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Clock,
  Package,
  MessageCircle,
  AppWindow,
  BarChart3,
  Workflow,
  Cable,
  Handshake,
  ArrowRight
} from 'lucide-react';
import SEO from '../components/SEO';

const WHATSAPP_URL =
  'https://wa.me/393806331704?text=Ciao%20Victor%2C%20ho%20visto%20i%20tuoi%20servizi%20e%20vorrei%20parlarti%20di%20un%20progetto.';

const OFFER_ICONS: Record<string, React.ElementType> = {
  app: AppWindow,
  dashboard: BarChart3,
  automation: Workflow,
  integration: Cable
};

const COMPANIES = ['Baker Hughes', 'Pharma Line', 'Focaccia Group', 'Angelica Wine'];

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } }
} as const;

const item = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } } as const;

type Offer = {
  code: string;
  name: string;
  tagline: string;
  timeline: string;
  bullets: string[];
  deliverable: string;
};

export const Services = () => {
  const { t } = useTranslation();

  const offers = (t('services.offers.items', { returnObjects: true }) as Offer[]) || [];
  const integratorBullets = (t('services.integrators.bullets', { returnObjects: true }) as string[]) || [];
  const steps = (t('services.process.steps', { returnObjects: true }) as { title: string; text: string }[]) || [];
  const stats = (t('services.proof.stats', { returnObjects: true }) as { value: string; label: string }[]) || [];
  const faqItems = (t('services.faq.items', { returnObjects: true }) as { q: string; a: string }[]) || [];

  return (
    <div className="pt-16 bg-white dark:bg-gray-900 min-h-screen">
      <SEO
        title={t('services.meta.seoTitle') as string}
        description={t('services.meta.seoDescription') as string}
        canonical="https://victorlazari.com/solutions"
        image="https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/VictorLazari-Media/Victor%20Lazari%20IT%20Consultant.png"
        type="website"
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[42rem] h-[24rem] rounded-full bg-sky-200/40 dark:bg-cyan-500/10 blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 text-center">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-sm font-semibold uppercase tracking-wider text-sky-600 dark:text-cyan-400">
              {t('services.hero.eyebrow')}
            </p>
            <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-gray-100 leading-tight">
              {t('services.hero.title')}
            </h1>
            <p className="mt-5 text-lg sm:text-xl text-slate-600 dark:text-gray-300 leading-relaxed">
              {t('services.hero.subtitle')}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2">
              {[t('services.hero.point1'), t('services.hero.point2'), t('services.hero.point3')].map((p, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 text-sm text-slate-700 dark:text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  {p as string}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 dark:bg-cyan-600 text-white font-semibold shadow-lg hover:bg-slate-800 dark:hover:bg-cyan-500 hover:-translate-y-0.5 transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                {t('services.hero.cta')}
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white dark:bg-gray-800 border border-slate-300 dark:border-gray-700 text-slate-900 dark:text-gray-100 font-semibold shadow-sm hover:border-slate-400 dark:hover:border-gray-500 hover:-translate-y-0.5 transition-all"
              >
                {t('services.hero.ctaSecondary')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Offers */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-gray-100">{t('services.offers.title')}</h2>
            <p className="mt-3 text-slate-600 dark:text-gray-300">{t('services.offers.subtitle')}</p>
          </motion.div>

          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {offers.map((offer) => {
              const Icon = OFFER_ICONS[offer.code] || AppWindow;
              return (
                <motion.article
                  key={offer.code}
                  variants={item}
                  className="group relative rounded-2xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-sky-300 dark:hover:border-cyan-700 transition-all flex flex-col"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="w-12 h-12 rounded-xl grid place-items-center bg-sky-50 dark:bg-cyan-900/30 text-sky-600 dark:text-cyan-400 border border-sky-100 dark:border-cyan-800">
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-gray-300">
                      <Clock className="w-3.5 h-3.5" />
                      {offer.timeline}
                    </span>
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-slate-900 dark:text-gray-100">{offer.name}</h3>
                  <p className="mt-2 text-sm text-slate-600 dark:text-gray-300 leading-relaxed">{offer.tagline}</p>
                  <ul className="mt-4 space-y-2 flex-1">
                    {(offer.bullets || []).map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-gray-300">
                        <CheckCircle2 className="w-4 h-4 text-sky-600 dark:text-cyan-400 mt-0.5 flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 pt-4 border-t border-slate-100 dark:border-gray-700 flex items-start gap-2">
                    <Package className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-slate-700 dark:text-gray-300">
                      <span className="font-semibold">{t('services.offers.deliverableLabel')}: </span>
                      {offer.deliverable}
                    </p>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* System integrators */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl border border-slate-200 dark:border-gray-700 bg-gradient-to-br from-slate-50 to-sky-50 dark:from-gray-800 dark:to-gray-800/60 p-8 md:p-10 grid md:grid-cols-[auto_1fr] gap-6 items-start"
          >
            <div className="w-14 h-14 rounded-2xl grid place-items-center bg-slate-900 dark:bg-cyan-600 text-white">
              <Handshake className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-gray-100">{t('services.integrators.title')}</h2>
              <p className="mt-3 text-slate-600 dark:text-gray-300 leading-relaxed">{t('services.integrators.text')}</p>
              <ul className="mt-4 grid sm:grid-cols-3 gap-3">
                {integratorBullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-sky-600 dark:text-cyan-400 mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-sky-700 dark:text-cyan-400 hover:text-sky-900 dark:hover:text-cyan-300 transition-colors"
              >
                {t('services.integrators.cta')}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process */}
      <section className="relative bg-slate-50 dark:bg-gray-800/40 border-y border-slate-100 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-gray-100">{t('services.process.title')}</h2>
            <p className="mt-2 text-slate-600 dark:text-gray-300">{t('services.process.subtitle')}</p>
          </motion.div>
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="mt-10 grid md:grid-cols-3 gap-6"
          >
            {steps.map((s, i) => (
              <motion.div
                key={i}
                variants={item}
                className="relative rounded-2xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-7"
              >
                <div className="text-5xl font-bold text-slate-100 dark:text-gray-700 absolute top-4 right-6 select-none" aria-hidden="true">
                  {i + 1}
                </div>
                <h3 className="relative text-lg font-bold text-slate-900 dark:text-gray-100">{s.title}</h3>
                <p className="relative mt-2 text-sm text-slate-600 dark:text-gray-300 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Proof */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center text-slate-900 dark:text-gray-100"
          >
            {t('services.proof.title')}
          </motion.h2>
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {stats.map((s, i) => (
              <motion.div key={i} variants={item} className="text-center">
                <div className="text-4xl font-bold text-sky-600 dark:text-cyan-400 tabular-nums">{s.value}</div>
                <p className="mt-2 text-sm text-slate-600 dark:text-gray-300 leading-snug">{s.label}</p>
              </motion.div>
            ))}
          </motion.div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            <span className="text-xs uppercase tracking-wider text-slate-400 dark:text-gray-500 font-medium">
              {t('services.proof.companiesLabel')}
            </span>
            {COMPANIES.map((c) => (
              <span key={c} className="text-base font-semibold text-slate-400 dark:text-gray-500">
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center text-slate-900 dark:text-gray-100 mb-8"
          >
            {t('services.faq.title')}
          </motion.h2>
          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true }} className="space-y-3">
            {faqItems.map((f, idx) => (
              <motion.details
                key={idx}
                variants={item}
                className="group rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-5 py-4 open:shadow-sm"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-3 font-semibold text-slate-900 dark:text-gray-100">
                  {f.q}
                  <span className="text-slate-400 group-open:rotate-45 transition-transform text-xl leading-none select-none" aria-hidden="true">+</span>
                </summary>
                <p className="mt-3 text-sm text-slate-600 dark:text-gray-300 leading-relaxed">{f.a}</p>
              </motion.details>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl bg-slate-900 dark:bg-gray-800 border border-slate-800 dark:border-gray-700 p-10 md:p-14 text-center relative overflow-hidden"
          >
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-96 h-64 rounded-full bg-cyan-500/20 blur-3xl pointer-events-none" aria-hidden="true" />
            <h2 className="relative text-3xl font-bold text-white">{t('services.finalCta.title')}</h2>
            <p className="relative mt-4 text-slate-300 max-w-2xl mx-auto leading-relaxed">{t('services.finalCta.copy')}</p>
            <div className="relative mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white text-slate-900 font-semibold shadow-lg hover:-translate-y-0.5 transition-all"
              >
                {t('services.finalCta.button')}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3.5 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                {t('services.finalCta.whatsapp')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
